import { Injectable, OnDestroy } from '@angular/core';
import { isEqual, pull } from '@sama/micro-dash';
import { SubscriptionManager } from '@sama/rxjs-core';
import { keys, pick } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { pickBy } from '../../../cross-team-shared/misc-utils/pick-by';
import { AbstractUndoManager } from '../../../cross-team-shared/state/abstract-undo-manager';
import { ShapeGroupOrderState } from '../../../team-apis/content/content-connector';
import { UndoRedoService } from '../../../team-apis/content/undo-redo-service';
import { AbstractOutput } from '../../../team-apis/otterdam/outputs/output';
import { TaskWorkspaceState } from '../../h3ml/state/task-workspace-state';
import {
  getShapeInfoFromOutputAnnotationInfo,
  getShapesRecordFromShapeInfo,
} from '../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';

const rootProperties = [
  'objects',
  'objectSelection',
  'objectTransientInfo',
  'shapeTransientInfo',
  'scopedObjectId',
  'shapeSelection',
] as const;
type RootPropertiesType = (typeof rootProperties)[number];

type UndoState = Pick<TaskWorkspaceState, RootPropertiesType> & {
  shapeInfo: StrictRecord<AbstractOutput['name'], ShapeGroupOrderState>;
};

/**
 * RootUndoService is the root level service that handles undoing and redoing the
 * taskWorkspaceStore state for object projects. It has the extra responsibility of propagating push/undo/redo
 * actions to child services.
 */
@Injectable()
export class RootUndoService
  extends AbstractUndoManager<TaskWorkspaceState, UndoState>
  implements OnDestroy
{
  override canUndo$ = new BehaviorSubject(false);
  override canRedo$ = new BehaviorSubject(false);

  private childUndoServices: UndoRedoService[] = [];
  private subscriptionManager = new SubscriptionManager();

  constructor(store: TaskWorkspaceStore) {
    super(store);
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  addChild(undoService: UndoRedoService) {
    this.childUndoServices.push(undoService);
  }

  removeChild(undoService: UndoRedoService) {
    pull(this.childUndoServices, undoService);
  }

  override canRedo() {
    return (
      super.canRedo() &&
      this.getChildUndoServices().every((service) => service.canRedo())
    );
  }

  override canUndo() {
    return (
      super.canUndo() &&
      this.getChildUndoServices().every((service) => service.canUndo())
    );
  }

  override pushCurrentState(
    options: {
      collectKey?: string | undefined;
      collectDebounce?: number | undefined;
    } = {},
  ) {
    const pushResults = this.getChildUndoServices().map((child) =>
      child.pushCurrentState(options),
    );
    pushResults.push(super.pushCurrentState(options));
    this.emitChanges();
    return {
      hasTrackedChanges: pushResults.some((result) => result.hasTrackedChanges),
    };
  }

  override undo() {
    // need to undo this first or else canUndo will return false because the
    // child can't undo anymore. more importantly, we need to undo the selection
    // first so that the annotation state undo service can calculate correctly
    // tagEditorHidden
    super.undo();
    this.childUndoServices.forEach((child) => child.undo());

    // need to recalculate canUndo$/canRedo$ because the children may have
    // changed their state
    this.emitChanges();
  }

  override redo() {
    // need to redo this first or else canRedo will return false because the
    // child can't redo anymore. more importantly, we need to undo the selection
    // first so that the annotation state undo service can calculate correctly
    // tagEditorHidden
    super.redo();
    this.childUndoServices.forEach((child) => child.redo());

    // need to recalculate canUndo$/canRedo$ because the children may have
    // changed their state
    this.emitChanges();
  }

  override reset() {
    super.reset();
    this.getChildUndoServices().forEach((child) => child.reset());
  }

  protected override applyUndoState(stateToApply: UndoState) {
    const objectSelection = pickBy(
      stateToApply.objectSelection,
      (_value, objectId) => stateToApply.objects[objectId],
    );

    this.store.getRootStore().batch(() => {
      this.store.assign(pick(stateToApply, rootProperties));
      this.store('objectSelection').set(objectSelection);
      this.applyShapeSelectionFromUndoState(stateToApply);

      keys(stateToApply.shapeInfo).forEach((outputName) => {
        this.store('outputAnnotationInfo')(outputName).assign(
          stateToApply.shapeInfo[outputName],
        );
      });
    });
  }

  protected override extractUndoState(state: TaskWorkspaceState): UndoState {
    return {
      ...pick(state, rootProperties),
      // @ts-ignore -- fix me
      shapeInfo: getShapeInfoFromOutputAnnotationInfo(
        state.outputAnnotationInfo,
      ),
    };
  }

  protected override hasTrackedChanges(
    before: UndoState | undefined,
    after: UndoState,
  ) {
    return !isEqual(
      pick(before, ['objects', 'shapeInfo']),
      pick(after, ['objects', 'shapeInfo']),
    );
  }

  private getChildUndoServices() {
    // unfortunately, the base constructor calls canUndo, canRedo and
    // pushCurrentState so the array is not initialized yet
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    return this.childUndoServices ?? [];
  }

  private emitChanges() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    this.canUndo$?.next(this.canUndo());
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    this.canRedo$?.next(this.canRedo());
  }

  private applyShapeSelectionFromUndoState(stateToApply: UndoState) {
    const allShapes = getShapesRecordFromShapeInfo(stateToApply.shapeInfo);
    const shapeSelection = pickBy(
      stateToApply.shapeSelection,
      (_value, shapeIndex) => allShapes[shapeIndex],
    );
    this.store('shapeSelection').set(shapeSelection);
  }
}
